// Modal editor for a column's lookup definition — the user-friendly
// counterpart to the inline fields. Mirrors ItemChangeRuleEditor: open it
// from the Property Panel's "Lookup" row, configure here, Save writes the
// lookup fields back onto the column.
//
// Two modes, toggled at the top:
//  - Table  : pick a source table + a display column + a value column from
//             dropdowns (composed into the `TABLE(DISPLAY, DATA)` spec), then
//             optionally narrow it with the structured filter builder.
//  - SQL    : write a full SELECT (advanced) — see LookupSqlEditor.
//
// For a column's own lookup (`matchRules`), Table mode also says how a WRITTEN
// value — a file import, the Assistant — becomes the code when it is not one:
// an optional cross-reference table of the other party's own codes (scoped by
// a value of the record, such as the order's customer), whether confirmed
// matches are remembered there, and a threshold for description matches.
// Without a cross-reference, matches are remembered as the master's aliases.
//
// The draft is local; nothing is committed to the model until Save, so the
// user can back out with Cancel.

import { useEffect, useMemo, useState } from 'react'
import Combobox, { type ComboboxOption } from './Combobox'
import type { ColumnDef, LookupFilterRules, LookupResolve, LookupXref } from '../txn/types'
import { compileLookupFilter } from '../txn/types'
import { type DesignerCatalog, mergeColumns, mergeTables } from './catalog'
import { modalOf, useKitConfirm } from './KitModal'
import LookupFilterEditor from './LookupFilterEditor'
import LookupSqlEditor from './LookupSqlEditor'

export interface LookupPatch {
  lookup: string
  lookup_sql?: string
  lookup_filter?: string
  lookup_filter_rules?: LookupFilterRules
  // Custom picker-window title (shared by both modes). Empty → default.
  lookup_title?: string
  // Transaction object opened by the picker's "+ Add" button (both modes).
  lookup_add_object?: string
  // SQL mode: which output column holds the stored value (name or 1-based
  // position). Empty → a column named `data`, else the first column.
  lookup_sql_data_col?: string
  // How a written value becomes the code (Table mode, column lookups only).
  lookup_xref?: LookupXref
  lookup_resolve?: LookupResolve
}

interface TransactionSummary { obj_name: string; title: string; main_table: string }

interface Props {
  open: boolean
  onClose: () => void
  // The column being edited — read its current lookup fields to seed the draft.
  column: ColumnDef | null
  // Current-form column names ({form.X}) and header column names ({header.X}).
  formColumns: string[]
  headerColumns: string[]
  // Project folder (the host's catalog is bound to it).
  project: string
  onSave: (patch: LookupPatch) => void
  /** Offer the "match written values" rules (a column's own lookup). */
  matchRules?: boolean
  /** Where table / column / transaction suggestions come from
   *  (designerKit/catalog). Absent ⇒ every picker degrades to free text. */
  catalog?: DesignerCatalog
  /** Longest window title the host can store (a report criterion's title is
   *  POPHELP.MSG_TITLE, 20 characters). Absent ⇒ no limit. */
  titleMaxLength?: number
}

interface TableSummary { name: string; description?: string }
interface ColumnSummary { column_name: string; data_type?: string }

const SPEC_RE =
  /^([A-Za-z_][A-Za-z0-9_]*)\s*\(\s*([A-Za-z_][A-Za-z0-9_]*)\s*,\s*([A-Za-z_][A-Za-z0-9_]*)\s*\)\s*$/

function parseSpec(spec: string | undefined): { table: string; disp: string; data: string } {
  const m = (spec || '').trim().match(SPEC_RE)
  if (!m) return { table: '', disp: '', data: '' }
  return { table: m[1], disp: m[2], data: m[3] }
}

// Quote a value as a SQL literal for the starter query.
function sqlLiteral(v: unknown): string {
  if (v == null) return 'NULL'
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return `'${String(v).replace(/'/g, "''")}'`
}

// Build a working SELECT draft from the declarative spec + filter so that
// switching into SQL mode starts from a query that already behaves the same.
function buildStarterSql(
  table: string, disp: string, data: string,
  filterRules: LookupFilterRules | undefined, rawFilter: string | undefined,
): string {
  const t = table || 'your_table'
  const d = disp || 'name'
  const v = data || 'id'
  let where = ''
  if (filterRules) {
    const compiled = compileLookupFilter(filterRules)
    let sql = compiled.sql
    Object.entries(compiled.literals).forEach(([k, val]) => {
      sql = sql.split(`{lit.${k}}`).join(sqlLiteral(val))
    })
    where = sql
  } else if ((rawFilter || '').trim()) {
    where = (rawFilter || '').trim()
  }
  const lines = [`SELECT ${d} AS display, ${v} AS data`, `FROM ${t}`]
  if (where) lines.push(`WHERE ${where}`)
  lines.push(`ORDER BY ${d}`)
  return lines.join('\n')
}

function cmpNoCase(a: string, b: string) {
  return a.toLowerCase().localeCompare(b.toLowerCase())
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
  marginBottom: 4,
  marginTop: 12,
}

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.8625rem',
  fontWeight: 600,
  color: 'var(--color-accent)',
  marginTop: 14,
  marginBottom: 6,
  paddingBottom: 4,
  borderBottom: '1px solid var(--color-border)',
}

const helpStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--color-text-secondary)',
  marginTop: 4,
}

const errorStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: '#ef4444',
  marginTop: 4,
  fontWeight: 500,
}

function modeBtn(active: boolean): React.CSSProperties {
  return {
    padding: '5px 14px',
    fontSize: '0.8425rem',
    border: '1px solid var(--color-border)',
    background: active ? 'var(--color-accent)' : 'var(--color-bg)',
    color: active ? '#fff' : 'var(--color-text)',
    cursor: 'pointer',
  }
}

/** Columns of a table: the catalog's live (deployed) columns merged with its
 *  designed ones (designed but maybe not deployed). */
async function loadTableColumns(catalog: DesignerCatalog, table: string): Promise<ColumnSummary[]> {
  const [live, design] = await Promise.allSettled([
    catalog.liveColumns ? catalog.liveColumns(table) : Promise.resolve([]),
    catalog.columns(table),
  ])
  return mergeColumns(
    live.status === 'fulfilled' ? live.value : [],
    design.status === 'fulfilled' ? design.value : [],
  )
}

export default function LookupEditor({
  open, onClose, column, formColumns, headerColumns, project, onSave, matchRules = false, catalog,
  titleMaxLength,
}: Props) {
  const [mode, setMode] = useState<'table' | 'sql'>('table')
  const Modal = modalOf(catalog)
  const { confirm, confirmDialog } = useKitConfirm(catalog)
  const [table, setTable] = useState('')
  const [disp, setDisp] = useState('')
  const [dataCol, setDataCol] = useState('')
  const [filterRules, setFilterRules] = useState<LookupFilterRules | undefined>(undefined)
  const [rawFilter, setRawFilter] = useState<string | undefined>(undefined)
  const [sql, setSql] = useState('')
  const [title, setTitle] = useState('')
  const [addObject, setAddObject] = useState('')
  const [sqlDataCol, setSqlDataCol] = useState('')
  // Match written values — cross-reference + threshold.
  const [xrefTable, setXrefTable] = useState('')
  const [xrefMatch, setXrefMatch] = useState('')
  const [xrefAlso, setXrefAlso] = useState('')
  const [xrefCode, setXrefCode] = useState('')
  const [xrefScopeCol, setXrefScopeCol] = useState('')
  const [xrefScopeRef, setXrefScopeRef] = useState('')
  const [xrefLearn, setXrefLearn] = useState(true)
  const [minScore, setMinScore] = useState('')
  const [xrefCols, setXrefCols] = useState<ColumnSummary[]>([])

  const [tables, setTables] = useState<TableSummary[]>([])
  const [cols, setCols] = useState<ColumnSummary[]>([])
  const [colsLoading, setColsLoading] = useState(false)
  const [txns, setTxns] = useState<TransactionSummary[]>([])

  const tableOptions = useMemo<ComboboxOption[]>(
    () => tables.map(t => ({ value: t.name, hint: t.description })),
    [tables],
  )
  const colOptions = useMemo<ComboboxOption[]>(
    () => cols.map(c => ({ value: c.column_name, hint: c.data_type })),
    [cols],
  )
  // Object picker options — label by obj_name, hint with title + table so
  // the author can tell forms apart at a glance.
  const txnOptions = useMemo<ComboboxOption[]>(
    () => txns.map(t => ({
      value: t.obj_name,
      hint: [t.title && t.title !== t.obj_name ? t.title : '', t.main_table]
        .filter(Boolean).join(' · ') || undefined,
    })),
    [txns],
  )

  // Default window title shown as a placeholder when none is set — mirrors
  // the runtime fallback ("Select <heading|name>").
  const defaultTitle = useMemo(
    () => `Select ${(column?.heading || column?.name || 'value').trim()}`,
    [column],
  )

  // Seed the draft from the column whenever the modal opens.
  useEffect(() => {
    if (!open) return
    const spec = parseSpec(column?.lookup)
    setTable(spec.table)
    setDisp(spec.disp)
    setDataCol(spec.data)
    setFilterRules(column?.lookup_filter_rules)
    setRawFilter(column?.lookup_filter)
    setSql(column?.lookup_sql || '')
    setTitle(column?.lookup_title || '')
    setAddObject(column?.lookup_add_object || '')
    setSqlDataCol(column?.lookup_sql_data_col || '')
    setMode((column?.lookup_sql || '').trim() ? 'sql' : 'table')
    const xr = column?.lookup_xref
    const [scopeCol, scopeRef] = Object.entries(xr?.scope || {})[0] || ['', '']
    setXrefTable(xr?.table || '')
    setXrefMatch(xr?.match_column || '')
    setXrefAlso((xr?.also_match || [])[0] || '')
    setXrefCode(xr?.code_column || '')
    setXrefScopeCol(scopeCol)
    setXrefScopeRef(scopeRef)
    setXrefLearn(xr ? xr.learn !== false : true)
    setMinScore(column?.lookup_resolve?.min_score != null ? String(column.lookup_resolve.min_score) : '')
  }, [open, column])

  // Columns of the cross-reference table.
  useEffect(() => {
    if (!open || !catalog || !matchRules) return
    const t = xrefTable.trim()
    if (!t) { setXrefCols([]); return }
    let cancelled = false
    const timer = window.setTimeout(() => {
      loadTableColumns(catalog, t)
        .then((c) => { if (!cancelled) setXrefCols(c) })
        .catch(() => { if (!cancelled) setXrefCols([]) })
    }, 250)
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [open, project, catalog, xrefTable, matchRules])

  const xrefColOptions = useMemo<ComboboxOption[]>(
    () => xrefCols.map(c => ({ value: c.column_name, hint: c.data_type })),
    [xrefCols],
  )
  const scopeRefOptions = useMemo(() => [
    ...headerColumns.map(c => ({ value: `header.${c}`, label: `${c} (header)` })),
    ...formColumns.map(c => ({ value: `form.${c}`, label: `${c} (this row)` })),
  ], [headerColumns, formColumns])

  // Load the project's transaction objects for the add-record picker.
  useEffect(() => {
    if (!open || !catalog?.transactions) { setTxns([]); return }
    let cancelled = false
    catalog.transactions()
      .then(res => { if (!cancelled) setTxns(Array.isArray(res) ? res : []) })
      .catch(() => { if (!cancelled) setTxns([]) })
    return () => { cancelled = true }
  }, [open, project, catalog])

  // Load project tables when the modal opens.
  useEffect(() => {
    if (!open || !catalog) return
    let cancelled = false
    // Merge two sources: the deployed tables (what a lookup can actually
    // target) and the designed catalog (tables designed but maybe not deployed
    // yet). Union by case-insensitive name.
    Promise.allSettled([
      catalog.liveTables ? catalog.liveTables() : Promise.resolve([]),
      catalog.tables(),
    ])
      .then(([preview, design]) => {
        if (cancelled) return
        // Live names first, then the designed ones' descriptions fill in.
        const live = preview.status === 'fulfilled' ? (preview.value || []).map(t => ({ name: t.name })) : []
        const designed = design.status === 'fulfilled' ? design.value || [] : []
        setTables(mergeTables(live, designed).sort((a, b) => cmpNoCase(a.name, b.name)))
      })
      .catch(() => { if (!cancelled) setTables([]) })
    return () => { cancelled = true }
  }, [open, project, catalog])

  // Load columns for the chosen table (debounced) to drive the display/value pickers.
  useEffect(() => {
    if (!open || !catalog) { return }
    const t = table.trim()
    if (!t) { setCols([]); setColsLoading(false); return }
    let cancelled = false
    setColsLoading(true)
    const timer = window.setTimeout(() => {
      loadTableColumns(catalog, t)
        .then((c) => { if (!cancelled) setCols(c) })
        .catch(() => { if (!cancelled) setCols([]) })
        .finally(() => { if (!cancelled) setColsLoading(false) })
    }, 250)
    return () => { cancelled = true; window.clearTimeout(timer) }
  }, [open, project, catalog, table])

  // Composed spec — also used as the synthetic column the filter builder reads.
  const composedSpec = useMemo(
    () => (table.trim() ? `${table.trim()}(${disp.trim()}, ${dataCol.trim()})` : ''),
    [table, disp, dataCol],
  )

  const filterColumn = useMemo<ColumnDef | null>(() => {
    if (!column) return null
    return {
      ...column,
      lookup: composedSpec,
      lookup_filter: rawFilter,
      lookup_filter_rules: filterRules,
    }
  }, [column, composedSpec, rawFilter, filterRules])

  const errors = useMemo(() => {
    const e: { table?: string; disp?: string; data?: string; sql?: string; xref?: string; title?: string } = {}
    if (mode === 'table') {
      if (!table.trim()) e.table = 'Pick a source table.'
      if (!disp.trim()) e.disp = 'Pick the column to display.'
      if (!dataCol.trim()) e.data = 'Pick the value (data) column.'
      if (matchRules && xrefTable.trim()) {
        if (!xrefMatch.trim() || !xrefCode.trim()) {
          e.xref = 'Pick the column holding their code and the column holding ours.'
        } else if (!!xrefScopeCol.trim() !== !!xrefScopeRef.trim()) {
          e.xref = 'A scope needs both the cross-reference column and the field it matches.'
        }
      }
      const n = minScore.trim() === '' ? null : Number(minScore)
      if (n != null && (Number.isNaN(n) || n < 0 || n > 1)) e.xref = 'The match threshold is a number from 0 to 1.'
    } else {
      if (!sql.trim()) e.sql = 'Write a SELECT query, or switch to Table mode.'
    }
    if (titleMaxLength && title.trim().length > titleMaxLength) {
      e.title = `Keep the title to ${titleMaxLength} characters or fewer.`
    }
    return e
  }, [mode, table, disp, dataCol, sql, matchRules, xrefTable, xrefMatch, xrefCode, xrefScopeCol, xrefScopeRef, minScore, title, titleMaxLength])

  const hasErrors = Object.values(errors).some(Boolean)

  const switchMode = async (next: 'table' | 'sql') => {
    if (next === mode) return
    if (next === 'sql') {
      // Seed a starter query the first time so the author has a draft.
      if (!sql.trim()) {
        setSql(buildStarterSql(table, disp, dataCol, filterRules, rawFilter))
      }
      setMode('sql')
    } else {
      if (sql.trim() && !(await confirm({
        title: 'Switch to Table mode?',
        message: 'Your SQL query will be cleared on save.',
        confirmLabel: 'Switch',
      }))) {
        return
      }
      setMode('table')
    }
  }

  // Clear the lookup entirely (the field becomes a plain input again).
  const handleRemove = () => {
    onSave({
      lookup: '', lookup_sql: '',
      lookup_filter: undefined, lookup_filter_rules: undefined,
      lookup_title: '', lookup_add_object: '', lookup_sql_data_col: '',
      lookup_xref: undefined, lookup_resolve: undefined,
    })
    onClose()
  }

  const hasAnything = !!(table.trim() || disp.trim() || dataCol.trim()
    || sql.trim() || filterRules || (rawFilter || '').trim()
    || title.trim() || addObject.trim() || sqlDataCol.trim())

  const handleSave = () => {
    if (hasErrors) return
    // Title and add-object are common to both modes.
    const common = {
      lookup_title: title.trim(),
      lookup_add_object: addObject.trim(),
    }
    if (mode === 'sql') {
      onSave({
        ...common,
        // SQL mode drives "+ Add" from the add-object picker, not a parsed
        // spec — so the declarative spec is cleared here.
        lookup: '',
        lookup_sql: sql.trim(),
        lookup_filter: rawFilter,       // ignored while SQL is set, kept for round-trip
        lookup_filter_rules: filterRules,
        lookup_sql_data_col: sqlDataCol.trim(),
      })
    } else {
      const xref: LookupXref | undefined = matchRules && xrefTable.trim() ? {
        table: xrefTable.trim(),
        match_column: xrefMatch.trim(),
        ...(xrefAlso.trim() ? { also_match: [xrefAlso.trim()] } : {}),
        code_column: xrefCode.trim(),
        ...(xrefScopeCol.trim() && xrefScopeRef.trim()
          ? { scope: { [xrefScopeCol.trim()]: xrefScopeRef.trim() } } : {}),
        learn: xrefLearn,
      } : undefined
      const resolve: LookupResolve | undefined = matchRules && minScore.trim() !== ''
        ? { min_score: Number(minScore) } : undefined
      onSave({
        ...common,
        lookup: composedSpec,
        lookup_sql: '',                 // clear → declarative mode is active
        lookup_filter: rawFilter,
        lookup_filter_rules: filterRules,
        lookup_sql_data_col: '',
        // A filter-condition lookup never carries match rules — keep the
        // column's own when this editor did not show them.
        lookup_xref: matchRules ? xref : column?.lookup_xref,
        lookup_resolve: matchRules ? resolve : column?.lookup_resolve,
      })
    }
    onClose()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Lookup"
      size="wide"
      footer={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%' }}>
          <button
            className="btn btn-ghost btn-sm"
            style={{ color: hasAnything ? '#ef4444' : 'var(--color-text-secondary)', visibility: hasAnything ? 'visible' : 'hidden' }}
            onClick={handleRemove}
          >
            Remove lookup
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-primary btn-sm"
              disabled={hasErrors}
              onClick={handleSave}
            >
              Save Lookup
            </button>
          </div>
        </div>
      }
    >
      {confirmDialog}
      <div>
        <div style={{ ...helpStyle, marginTop: 0, marginBottom: 8 }}>
          A lookup lets the user pick this field&apos;s value from another table.
          Choose a table and which columns to show and store, or write your own
          SQL query for full control.
        </div>

        {/* Window title — common to both modes. */}
        <label style={{ ...labelStyle, marginTop: 0 }}>Lookup window title (optional)</label>
        <input
          type="text"
          value={title}
          onChange={e => setTitle(e.target.value)}
          maxLength={titleMaxLength}
          placeholder={defaultTitle}
          aria-label="Lookup window title"
          style={{
            width: '100%', padding: '6px 8px',
            background: 'var(--color-bg)', border: '1px solid var(--color-border)',
            borderRadius: 4, color: 'var(--color-text)', fontSize: '0.8625rem',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ ...helpStyle, marginBottom: 10 }}>
          Shown at the top of the picker window. Leave blank to use{' '}
          <code>{defaultTitle}</code>.
          {titleMaxLength ? ` Up to ${titleMaxLength} characters (${title.trim().length}/${titleMaxLength}).` : ''}
        </div>
        {errors.title && <div style={{ ...errorStyle, marginTop: -6, marginBottom: 10 }}>{errors.title}</div>}

        {/* Mode toggle */}
        <div style={{ display: 'inline-flex', borderRadius: 4, overflow: 'hidden', marginBottom: 4 }}>
          <button
            type="button"
            style={{ ...modeBtn(mode === 'table'), borderRight: 'none', borderTopLeftRadius: 4, borderBottomLeftRadius: 4 }}
            onClick={() => void switchMode('table')}
          >Table</button>
          <button
            type="button"
            style={{ ...modeBtn(mode === 'sql'), borderTopRightRadius: 4, borderBottomRightRadius: 4 }}
            onClick={() => void switchMode('sql')}
          >SQL query</button>
        </div>

        {mode === 'table' ? (
          <>
            <div style={sectionTitleStyle}>1. Source table</div>
            <Combobox
              value={table}
              onChange={setTable}
              options={tableOptions}
              placeholder="Type or pick a table — e.g. STATES"
              invalid={!!errors.table}
              ariaLabel="Source table"
              emptyText="No tables found — type a table name."
            />
            <div style={helpStyle}>
              Type to search, or click the ▾ to browse every table in this project.
            </div>
            {errors.table && <div style={errorStyle}>{errors.table}</div>}

            <div style={sectionTitleStyle}>2. Columns</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ ...labelStyle, marginTop: 0 }}>Display column (what the user sees)</label>
                <Combobox
                  value={disp}
                  onChange={setDisp}
                  options={colOptions}
                  loading={colsLoading}
                  placeholder="e.g. state_name"
                  invalid={!!errors.disp}
                  ariaLabel="Display column"
                  emptyText={table.trim() ? 'No columns found — type a name.' : 'Pick a table first.'}
                />
                {errors.disp && <div style={errorStyle}>{errors.disp}</div>}
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ ...labelStyle, marginTop: 0 }}>Value column (what gets stored)</label>
                <Combobox
                  value={dataCol}
                  onChange={setDataCol}
                  options={colOptions}
                  loading={colsLoading}
                  placeholder="e.g. state_code"
                  invalid={!!errors.data}
                  ariaLabel="Value column"
                  emptyText={table.trim() ? 'No columns found — type a name.' : 'Pick a table first.'}
                />
                {errors.data && <div style={errorStyle}>{errors.data}</div>}
              </div>
            </div>

            <div style={sectionTitleStyle}>3. Filter (optional)</div>
            {composedSpec && filterColumn ? (
              <LookupFilterEditor
                column={filterColumn}
                formColumns={formColumns}
                headerColumns={headerColumns}
                project={project}
                catalog={catalog}
                onChange={(patch) => {
                  // Mirror PropertyPanel's "exactly one of rules/sql" merge.
                  if ('lookup_filter_rules' in patch) {
                    setFilterRules(patch.lookup_filter_rules)
                  }
                  if ('lookup_filter' in patch) {
                    setRawFilter(patch.lookup_filter)
                  }
                }}
              />
            ) : (
              <div style={{ ...helpStyle, fontStyle: 'italic' }}>
                Pick a source table first to add a filter.
              </div>
            )}

            {matchRules && (
              <>
                <div style={sectionTitleStyle}>4. Match written values (optional)</div>
                <div style={{ ...helpStyle, marginTop: 0 }}>
                  When an import or the Assistant brings a name, a description or
                  the <em>other party&apos;s own code</em> (a customer&apos;s part
                  number) instead of yours, it is turned into your code: exact
                  name, then this cross-reference, then the closest record by
                  description. Without a cross-reference, matches a person
                  confirms are remembered as aliases of the record.
                </div>

                <label style={labelStyle}>Cross-reference table</label>
                <Combobox
                  value={xrefTable}
                  onChange={setXrefTable}
                  options={tableOptions}
                  placeholder="e.g. CUSTOMER_ITEM_XREF — leave blank for none"
                  ariaLabel="Cross-reference table"
                  emptyText="No tables found — type a table name."
                />
                {xrefTable.trim() && (
                  <>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Their code</label>
                        <Combobox value={xrefMatch} onChange={setXrefMatch} options={xrefColOptions}
                          placeholder="e.g. cust_item_code" ariaLabel="Their code column"
                          emptyText="No columns found — type a name." />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Their description (optional)</label>
                        <Combobox value={xrefAlso} onChange={setXrefAlso} options={xrefColOptions}
                          placeholder="e.g. cust_item_descr" ariaLabel="Their description column"
                          emptyText="No columns found — type a name." />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Our code</label>
                        <Combobox value={xrefCode} onChange={setXrefCode} options={xrefColOptions}
                          placeholder={dataCol || 'e.g. item_code'} ariaLabel="Our code column"
                          emptyText="No columns found — type a name." />
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Only for (cross-reference column)</label>
                        <Combobox value={xrefScopeCol} onChange={setXrefScopeCol} options={xrefColOptions}
                          placeholder="e.g. cust_code — blank = everyone" ariaLabel="Scope column"
                          emptyText="No columns found — type a name." />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={labelStyle}>…matching this record&apos;s</label>
                        <select
                          value={xrefScopeRef}
                          onChange={e => setXrefScopeRef(e.target.value)}
                          aria-label="Scope field"
                          style={{
                            width: '100%', padding: '6px 8px', background: 'var(--color-bg)',
                            border: '1px solid var(--color-border)', borderRadius: 4,
                            color: 'var(--color-text)', fontSize: '0.8625rem',
                          }}
                        >
                          <option value="">(no scope)</option>
                          {scopeRefOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                    </div>
                    <div style={helpStyle}>
                      A customer&apos;s part number means something only for that
                      customer — scope the match by the order&apos;s customer so
                      two customers&apos; &ldquo;ABC-12&rdquo; never mix.
                    </div>

                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.8125rem', marginTop: 10 }}>
                      <input type="checkbox" checked={xrefLearn} onChange={e => setXrefLearn(e.target.checked)} />
                      Remember matches a person confirms in an import (adds rows to this table)
                    </label>
                  </>
                )}

                <label style={labelStyle}>Accept a description match from (optional)</label>
                <input
                  type="number" min={0} max={1} step={0.05}
                  value={minScore}
                  onChange={e => setMinScore(e.target.value)}
                  placeholder="the master's setting (default 0.55)"
                  aria-label="Description match threshold"
                  style={{
                    width: 220, padding: '6px 8px', background: 'var(--color-bg)',
                    border: '1px solid var(--color-border)', borderRadius: 4,
                    color: 'var(--color-text)', fontSize: '0.8625rem',
                  }}
                />
                {errors.xref && <div style={errorStyle}>{errors.xref}</div>}
              </>
            )}
          </>
        ) : (
          <>
            <LookupSqlEditor
              value={sql}
              formColumns={formColumns}
              headerColumns={headerColumns}
              selfColumn={column?.name}
              onChange={setSql}
            />
            {errors.sql && <div style={errorStyle}>{errors.sql}</div>}

            <div style={sectionTitleStyle}>Value column</div>
            <input
              type="text"
              value={sqlDataCol}
              onChange={e => setSqlDataCol(e.target.value)}
              placeholder="e.g. state_code, or a position like 2"
              aria-label="Value (data) column"
              style={{
                width: '100%', padding: '6px 8px',
                background: 'var(--color-bg)', border: '1px solid var(--color-border)',
                borderRadius: 4, color: 'var(--color-text)', fontSize: '0.8625rem',
                fontFamily: 'monospace', boxSizing: 'border-box',
              }}
            />
            <div style={helpStyle}>
              Your query can select several columns — they all show in the
              picker. Name the column (or its 1-based position) whose value is
              stored when the user clicks a row. Leave blank to use a column
              named <code>data</code>, or the first column.
            </div>
          </>
        )}

        {/* Add-record object — common to both modes. */}
        <div style={sectionTitleStyle}>Add new record (optional)</div>
        <Combobox
          value={addObject}
          onChange={setAddObject}
          options={txnOptions}
          placeholder="Pick the form to open — e.g. COUNTRY"
          ariaLabel="Add-record object"
          emptyText="No transaction objects found in this project."
        />
        <div style={helpStyle}>
          When set, the picker shows a &ldquo;+ Add&rdquo; button that opens
          this object&apos;s form so the user can create a new record and
          select it without leaving the page — e.g. add a missing country.
          {mode === 'table'
            ? ' Leave blank to fall back to the source table’s own form when one exists.'
            : ' Leave blank to hide the “+ Add” button.'}
        </div>
      </div>
    </Modal>
  )
}
